import { authenticatedFetch } from "../util/authenticatedFetch";
import { SUPPORT_ACCESS_DENIED_MESSAGE } from "../util/supportAccess";
import { parseSupportDate } from "../pages/support/supportFormatters";

export class SupportApiError extends Error {
  constructor(message, { status = null, details = null } = {}) {
    super(message);
    this.name = "SupportApiError";
    this.status = status;
    this.details = details;
  }
}

const apiUrl = (path) => {
  const base = process.env.REACT_APP_BACKEND_API_URL || "";
  return `${base.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
};

const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

const abortError = () => {
  const error = new Error("Support API request was cancelled.");
  error.name = "AbortError";
  return error;
};

const readPayload = async (response) => {
  if (response.status === 204) return null;

  const contentType = response.headers?.get?.("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  try {
    const text = await response.text();
    return text ? { message: text } : null;
  } catch (_error) {
    return null;
  }
};

const request = async (path, options = {}) => {
  const {
    signal: externalSignal,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    ...fetchOptions
  } = options;
  const headers = new Headers(fetchOptions.headers || {});
  headers.set("Accept", "application/json");
  if (fetchOptions.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const controller = new AbortController();
  let timeoutId = null;
  let handleExternalAbort = null;
  let rejectCancellation;
  const cancellation = new Promise((_, reject) => {
    rejectCancellation = reject;
  });

  handleExternalAbort = () => {
    controller.abort();
    rejectCancellation(abortError());
  };
  if (externalSignal) {
    if (externalSignal.aborted) handleExternalAbort();
    else externalSignal.addEventListener("abort", handleExternalAbort, { once: true });
  }
  if (Number.isFinite(Number(timeoutMs)) && Number(timeoutMs) > 0) {
    timeoutId = window.setTimeout(() => {
      controller.abort();
      rejectCancellation(
        new SupportApiError(
          `Support API request timed out after ${Math.ceil(
            Number(timeoutMs) / 1000
          )} seconds.`
        )
      );
    }, Number(timeoutMs));
  }

  try {
    const response = await Promise.race([
      authenticatedFetch(apiUrl(path), {
        credentials: "include",
        ...fetchOptions,
        headers,
        signal: controller.signal,
      }),
      cancellation,
    ]);
    const payload = await Promise.race([readPayload(response), cancellation]);

    if (!response.ok) {
      const message =
        response.status === 403
          ? SUPPORT_ACCESS_DENIED_MESSAGE
          : payload?.message ||
            payload?.detail ||
            `Support API request failed (${response.status})`;
      throw new SupportApiError(message, {
        status: response.status,
        details: payload,
      });
    }

    return payload;
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    if (externalSignal && handleExternalAbort) {
      externalSignal.removeEventListener("abort", handleExternalAbort);
    }
  }
};

const finiteMetric = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const derivedSuccessRate = (successful, failed) => {
  if (successful === null || failed === null) return undefined;
  const attempts = successful + failed;
  return attempts > 0 ? successful / attempts : undefined;
};

export const normalizeSupportPipeline = (pipelineValue = {}, fallback = {}) => {
  const pipeline = pipelineValue && typeof pipelineValue === "object" ? pipelineValue : {};
  const successful1h = finiteMetric(
    pipeline.success1h,
    pipeline.successfulLastHour,
    fallback.success1h,
    fallback.successfulLastHour
  );
  const failed1h = finiteMetric(
    pipeline.failed1h,
    pipeline.failedLastHour,
    fallback.failed1h,
    fallback.failedLastHour
  );
  const successful24h = finiteMetric(
    pipeline.success24h,
    pipeline.successfulLast24Hours,
    fallback.success24h,
    fallback.successfulLast24Hours
  );
  const failed24h = finiteMetric(
    pipeline.failed24h,
    pipeline.failedLast24Hours,
    fallback.failed24h,
    fallback.failedLast24Hours
  );
  const received24h = finiteMetric(
    pipeline.received24h,
    pipeline.receivedLast24Hours,
    fallback.received24h,
    fallback.receivedLast24Hours
  );
  const derivedThroughput =
    successful24h !== null && failed24h !== null
      ? (successful24h + failed24h) / (24 * 60)
      : received24h !== null
      ? received24h / (24 * 60)
      : undefined;

  return {
    ...pipeline,
    received1h:
      pipeline.received1h ?? pipeline.receivedLastHour ?? pipeline.receivedLast1Hour ?? fallback.received1h ?? fallback.receivedLastHour,
    received24h:
      pipeline.received24h ?? pipeline.receivedLast24Hours ?? fallback.receivedLast24Hours,
    success24h:
      pipeline.success24h ??
      pipeline.successfulLast24Hours ??
      fallback.success24h ??
      fallback.successfulLast24Hours,
    failed24h:
      pipeline.failed24h ??
      pipeline.failedLast24Hours ??
      fallback.failed24h ??
      fallback.failedLast24Hours,
    successRate1h:
      pipeline.successRate1h ??
      pipeline.successRateLastHour ??
      pipeline.successRateLast1Hour ??
      fallback.successRate1h ??
      fallback.successRateLastHour ??
      fallback.successRateLast1Hour ??
      derivedSuccessRate(successful1h, failed1h),
    successRate24h:
      pipeline.successRate24h ??
      pipeline.successRateLast24Hours ??
      fallback.successRate24h ??
      fallback.successRateLast24Hours ??
      derivedSuccessRate(successful24h, failed24h),
    throughputPerMinute:
      pipeline.throughputPerMinute ??
      pipeline.throughputPerMinuteLast24Hours ??
      fallback.throughputPerMinute ??
      fallback.throughputPerMinuteLast24Hours ??
      derivedThroughput,
    queued: pipeline.queued ?? fallback.queued,
    processing: pipeline.processing ?? fallback.processing,
    retryWait: pipeline.retryWait ?? fallback.retryWait,
    manualReview: pipeline.manualReview ?? fallback.manualReview,
    blocked: pipeline.blocked ?? fallback.blocked,
    p50SyncMs:
      pipeline.p50SyncMs ?? pipeline.p50SyncLatencyMs ?? fallback.p50SyncLatencyMs,
    p95SyncMs:
      pipeline.p95SyncMs ?? pipeline.p95SyncLatencyMs ?? fallback.p95SyncLatencyMs,
    p99SyncMs:
      pipeline.p99SyncMs ?? pipeline.p99SyncLatencyMs ?? fallback.p99SyncLatencyMs,
    oldestQueuedAt:
      pipeline.oldestQueuedAt ?? pipeline.oldestWorkAt ?? fallback.oldestWorkAt,
    lastSuccessfulAt:
      pipeline.lastSuccessfulAt ??
      pipeline.lastSuccessfulSyncAt ??
      pipeline.lastSyncedAt ??
      fallback.lastSyncedAt,
  };
};

const normalizeSupportOnboarding = (onboardingValue = {}, fallback = {}) => {
  const onboarding =
    onboardingValue && typeof onboardingValue === "object" ? onboardingValue : {};
  const status =
    onboarding.status ||
    fallback.onboardingStatus ||
    fallback.lifecycleStatus ||
    (onboarding.completedAt || fallback.onboardingCompletedAt
      ? "COMPLETED"
      : onboarding.startedAt || fallback.onboardingStartedAt
      ? "IN_PROGRESS"
      : null);
  const guideExpiresAt =
    onboarding.guideExpiresAt || fallback.onboardingGuideExpiresAt;
  const explicitGuideVisible =
    typeof onboarding.guideVisible === "boolean"
      ? onboarding.guideVisible
      : typeof onboarding.showGuide === "boolean"
      ? onboarding.showGuide
      : typeof fallback.guideVisible === "boolean"
      ? fallback.guideVisible
      : typeof fallback.showGuide === "boolean"
      ? fallback.showGuide
      : undefined;
  const parsedGuideExpiry = guideExpiresAt
    ? parseSupportDate(guideExpiresAt).getTime()
    : Number.NaN;
  const guideVisible =
    explicitGuideVisible ??
    (String(status || "").toUpperCase() === "COMPLETED" &&
    Number.isFinite(parsedGuideExpiry)
      ? parsedGuideExpiry > Date.now()
      : undefined);

  return {
    ...onboarding,
    status,
    startedAt: onboarding.startedAt || fallback.onboardingStartedAt,
    currentStep: onboarding.currentStep ?? fallback.onboardingCurrentStep,
    deferredAt: onboarding.deferredAt || fallback.onboardingDeferredAt,
    completedAt: onboarding.completedAt || fallback.onboardingCompletedAt,
    guideExpiresAt,
    historyDays: onboarding.historyDays ?? fallback.onboardingHistoryDays,
    importStatus: onboarding.importStatus || fallback.onboardingImportStatus,
    importedOrders: onboarding.importedOrders ?? fallback.onboardingImportedOrders,
    importTotalOrders:
      onboarding.importTotalOrders ?? fallback.onboardingImportTotalOrders,
    merchantScreen: onboarding.merchantScreen || fallback.merchantScreen,
    showGuide:
      typeof onboarding.showGuide === "boolean" ? onboarding.showGuide : guideVisible,
    guideVisible:
      typeof onboarding.guideVisible === "boolean"
        ? onboarding.guideVisible
        : guideVisible,
    importPendingOrders:
      onboarding.importPendingOrders ?? fallback.onboardingImportPendingOrders,
    paypalConnected: onboarding.paypalConnected,
    autoSyncEnabled: onboarding.autoSyncEnabled,
  };
};

export const normalizeSupportStore = (storeValue = {}) => {
  const store = storeValue && typeof storeValue === "object" ? storeValue : {};
  const trial = store.trial === true || store.onTrial === true;
  const subscriptionActive = store.subscriptionActive === true;
  const health = store.health || store.healthStatus || store.status || "UNKNOWN";
  const activePayPalAccounts = Number(
    store.activePayPalAccounts ??
      (Array.isArray(store.paypalAccounts) ? store.paypalAccounts.length : store.paypalAccounts) ??
      0
  );
  const rateLimitedPayPalAccounts = Number(store.rateLimitedPayPalAccounts ?? 0);
  const unhealthyPayPalAccounts = Number(store.unhealthyPayPalAccounts ?? 0);
  const onboarding =
    store.onboarding && typeof store.onboarding === "object" ? store.onboarding : {};
  const disputes =
    store.disputes && typeof store.disputes === "object" && !Array.isArray(store.disputes)
      ? store.disputes
      : store.disputeSummary && typeof store.disputeSummary === "object"
      ? store.disputeSummary
      : {};
  return {
    ...store,
    id: store.id ?? store.shopId ?? store.storeId ?? null,
    email: store.email || store.contactEmail,
    owner: store.owner || store.ownerName,
    healthStatus: health,
    issueSummary:
      store.issueSummary ||
      (Array.isArray(store.issueCodes) && store.issueCodes.length
        ? store.issueCodes
            .map((code) =>
              String(code)
                .toLowerCase()
                .split(/[_\s-]+/)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ")
            )
            .join(" · ")
        : null),
    lastSuccessfulSyncAt:
      store.lastSuccessfulSyncAt || store.lastSyncedAt || store.lastSyncAt,
    connections: {
      ...(store.connections || {}),
      shopify: {
        ...(store.connections?.shopify || {}),
        status:
          store.connections?.shopify?.status ||
          (store.shopifyConnected === true ? "CONNECTED" : "DISCONNECTED"),
        connected: store.shopifyConnected,
        sessionAvailable: store.shopifySessionAvailable,
        credentialUsable: store.shopifySessionTokenUsable,
        legacyCredentialOnly: store.legacyShopifyCredentialOnly,
      },
      paypal: {
        ...(store.connections?.paypal || {}),
        status:
          store.connections?.paypal?.status ||
          store.paypalConnectionHealth ||
          (activePayPalAccounts > 0 ? "CONNECTED" : "DISCONNECTED"),
        connected: activePayPalAccounts > 0,
        activeAccounts: activePayPalAccounts,
        rateLimitedAccounts: rateLimitedPayPalAccounts,
        rateLimited: rateLimitedPayPalAccounts > 0,
        unhealthyAccounts: unhealthyPayPalAccounts,
        connectionHealth: store.paypalConnectionHealth,
      },
    },
    billing: {
      ...(store.billing || {}),
      status:
        store.billing?.status ||
        (trial ? "TRIAL" : subscriptionActive ? "ACTIVE" : "INACTIVE"),
      active: subscriptionActive,
      trial,
      model: store.billingModel || store.billing?.model,
      tier: store.planName || store.billing?.tier || store.billingModel,
      periodStart: store.cycleStart || store.billing?.periodStart,
      periodEnd: store.cycleEnd || store.billing?.periodEnd,
      ordersProcessed: store.ordersProcessedInCycle ?? store.billing?.ordersProcessed,
      amount: store.billedTotalInCycle ?? store.billing?.amount,
      trialEndsAt:
        store.trialEndsAt || store.billing?.trialEndsAt || (trial ? store.cycleEnd : null),
      pendingEntries: store.pendingBillingEntries ?? store.billing?.pendingEntries,
      failedEntries: store.failedBillingEntries ?? store.billing?.failedEntries,
      shopifySubscriptionId:
        store.shopifySubscriptionId || store.billing?.shopifySubscriptionId,
      subscriptionLineItemId:
        store.subscriptionLineItemId || store.billing?.subscriptionLineItemId,
    },
    pipeline: normalizeSupportPipeline(store.pipeline || {}, store),
    configuration: {
      ...(store.configuration || {}),
      digitalOrdersEnabled: store.digitalSync ?? store.configuration?.digitalOrdersEnabled,
      carrierMappingEnabled: store.courierMapping ?? store.configuration?.carrierMappingEnabled,
      courierMatcher: store.courierMatcher ?? store.configuration?.courierMatcher,
      syncEnabled: store.syncEnabled ?? store.configuration?.syncEnabled,
      notifyBuyer: store.notifyBuyer ?? store.configuration?.notifyBuyer,
      webhookStatus: store.webhookStatus || store.configuration?.webhookStatus,
    },
    onboarding: normalizeSupportOnboarding(onboarding, store),
    disputes: {
      ...disputes,
      total: disputes.total ?? store.totalDisputes,
      open: disputes.open ?? store.openDisputes,
      attention: disputes.attention ?? disputes.responseDue ?? store.disputesNeedingAttention,
      overdue: disputes.overdue ?? store.overdueDisputes,
      lastSyncedAt: disputes.lastSyncedAt || store.disputesLastSyncedAt,
      syncStatus: disputes.syncStatus || store.disputesSyncStatus,
      underReview: disputes.underReview ?? store.disputesUnderReview,
      won: disputes.won ?? store.disputesWon,
      lost: disputes.lost ?? store.disputesLost,
      dueWithin24Hours: disputes.dueWithin24Hours ?? store.disputesDueWithin24Hours,
      failedActions: disputes.failedActions ?? store.failedDisputeActions,
      failedWebhookEvents: disputes.failedWebhookEvents ?? store.failedDisputeWebhookEvents,
      pendingWebhookEvents: disputes.pendingWebhookEvents ?? store.pendingDisputeWebhookEvents,
      syncError: disputes.syncError || store.disputesSyncError,
    },
  };
};

const normalizeOverview = (payload) => {
  const value = payload && typeof payload === "object" ? payload : {};
  const pipeline = value.pipeline && typeof value.pipeline === "object" ? value.pipeline : {};
  const stores = value.stores && !Array.isArray(value.stores) ? value.stores : {};
  return {
    ...value,
    pipeline: normalizeSupportPipeline(pipeline, value),
    recentStores: Array.isArray(value.recentStores)
      ? value.recentStores.map(normalizeSupportStore)
      : [],
    attentionStores: Array.isArray(value.attentionStores)
      ? value.attentionStores.map(normalizeSupportStore)
      : [],
    components: Array.isArray(value.components)
      ? value.components.map((component) => ({
          ...component,
          name: component?.name || component?.key,
          summary: component?.summary || component?.message || component?.details,
          lastHeartbeatAt: component?.lastHeartbeatAt || component?.heartbeatAt,
        }))
      : [],
    stores: {
      ...stores,
      inactive: stores.inactive ?? stores.disconnected ?? value.disconnected,
      new24h: stores.new24h ?? stores.newLast24Hours ?? value.newLast24Hours,
      new7d:
        stores.new7d ??
        stores.newLast7Days,
    },
  };
};

const normalizeSearch = (payload) => {
  const value = payload && typeof payload === "object" ? payload : {};
  return {
    ...value,
    content: (Array.isArray(value.content)
      ? value.content
      : Array.isArray(value.stores)
      ? value.stores
      : []
    ).map(normalizeSupportStore),
  };
};

export const getSupportOverview = ({ signal } = {}) =>
  request("/support/overview", { method: "GET", signal }).then(normalizeOverview);

export const getSupportSyncHealth = ({ shopId, merchantAccountId, signal } = {}) => {
  const scoped = shopId !== null && shopId !== undefined && shopId !== "";
  const path = scoped
    ? `/support/stores/${encodeURIComponent(shopId)}/sync-health`
    : "/support/sync-health";
  const params = new URLSearchParams();
  if (scoped && merchantAccountId !== null && merchantAccountId !== undefined) {
    const normalizedAccount = String(merchantAccountId).trim();
    if (normalizedAccount) params.set("merchantAccountId", normalizedAccount);
  }
  const query = params.toString();
  return request(`${path}${query ? `?${query}` : ""}`, {
    method: "GET",
    signal,
  }).then((payload) => (payload && typeof payload === "object" ? payload : {}));
};

export const searchSupportStores = (search, { signal } = {}) =>
  request("/support/stores/search", {
    method: "POST",
    signal,
    body: JSON.stringify(search),
  }).then(normalizeSearch);

export const getSupportStoreWorkspace = (shopId, { signal } = {}) =>
  request(`/support/stores/${encodeURIComponent(shopId)}/workspace`, {
    method: "GET",
    signal,
  }).then((payload) => {
    const value = payload && typeof payload === "object" ? payload : {};
    const store = normalizeSupportStore(value.store || value.storeDetails || {});
    const disputesValue =
      value.disputes && typeof value.disputes === "object" && !Array.isArray(value.disputes)
        ? value.disputes
        : value.disputeSummary && typeof value.disputeSummary === "object"
        ? value.disputeSummary
        : store.disputes || {};
    return {
      ...value,
      store,
      lifecycle:
        value.lifecycle && typeof value.lifecycle === "object"
          ? value.lifecycle
          : {
              active: store.active,
              installedAt: store.installedAt,
              lastActivityAt: store.lastActivityAt,
              lastImportedAt: store.lastImportedAt,
              lastSyncedAt: store.lastSyncedAt,
              deactivatedAt: store.deactivatedAt,
              deactivationReason: store.deactivationReason,
              lastProcessedAtPullAt: store.lastProcessedAtPullAt,
              lastPullStatus: store.lastPullStatus,
              lastPullErrorAt: store.lastPullErrorAt,
            },
      pipeline: normalizeSupportPipeline(value.pipeline || store.pipeline || {}, store),
      connections: value.connections || store.connections,
      billing: value.billing || store.billing,
      configuration: value.configuration || store.configuration,
      carrierRules: Array.isArray(value.carrierRules)
        ? value.carrierRules
        : Array.isArray(value.carrierMappings)
        ? value.carrierMappings
        : [],
      recentOrders: Array.isArray(value.recentOrders) ? value.recentOrders : [],
      onboarding: normalizeSupportOnboarding(
        value.onboarding && typeof value.onboarding === "object"
          ? value.onboarding
          : store.onboarding,
        store
      ),
      disputes: disputesValue,
      recentDisputes: Array.isArray(value.recentDisputes)
        ? value.recentDisputes
        : Array.isArray(value.disputes)
        ? value.disputes
        : Array.isArray(disputesValue?.cases)
        ? disputesValue.cases
        : Array.isArray(disputesValue?.recent)
        ? disputesValue.recent
        : [],
      operationalIncidents: Array.isArray(value.operationalIncidents)
        ? value.operationalIncidents
        : Array.isArray(value.incidents)
        ? value.incidents
        : [],
    };
  });

const disputeQuery = (search = {}) => {
  const params = new URLSearchParams();
  Object.entries(search || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (Array.isArray(value)) value.forEach((item) => params.append(key, String(item)));
    else params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : "";
};

export const getSupportStoreDisputes = (shopId, search = {}, { signal } = {}) =>
  request(
    `/support/stores/${encodeURIComponent(shopId)}/disputes${disputeQuery(search)}`,
    { method: "GET", signal }
  ).then((payload) => {
    const value = payload && typeof payload === "object" ? payload : {};
    return {
      ...value,
      content: Array.isArray(value.content)
        ? value.content
        : Array.isArray(value.disputes)
        ? value.disputes
        : [],
      summary:
        value.summary && typeof value.summary === "object" ? value.summary : {},
    };
  });

export const getSupportStoreDispute = (shopId, disputeId, { signal } = {}) =>
  request(
    `/support/stores/${encodeURIComponent(shopId)}/disputes/${encodeURIComponent(disputeId)}`,
    { method: "GET", signal }
  ).then((payload) => (payload && typeof payload === "object" ? payload : {}));

export const supportApi = {
  getOverview: getSupportOverview,
  getSyncHealth: getSupportSyncHealth,
  searchStores: searchSupportStores,
  getStoreWorkspace: getSupportStoreWorkspace,
  getStoreDisputes: getSupportStoreDisputes,
  getStoreDispute: getSupportStoreDispute,
};

export default supportApi;
